import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Darstellung des Inhaltsverzeichnisses — abgeleitet aus der **gemessenen
 * Container-Breite**, nicht aus dem Viewport und nicht aus `chrome`.
 *
 * Warum nicht Viewport: das Risiko-Formular läuft in einem schwebenden
 * Fenster. Der Viewport ist dort breit, das Fenster nicht — eine
 * `@media (min-width: 1024px)`-Regel liefert im Fenster also systematisch die
 * falsche Antwort.
 *
 * Warum nicht `chrome === "embedded"`: genau diese Kopplung sorgte dafür, dass
 * das Verzeichnis ausgerechnet im internen Fenster fehlte und nur der Kunde im
 * öffentlichen Formular es sah.
 */
export type TocMode = "auto" | "none";
export type TocLayout = "sidebar" | "menu" | "none";

/** Ab dieser Container-Breite steht das Verzeichnis dauerhaft links. */
export const TOC_SIDEBAR_MIN_WIDTH = 1200;
/** Darunter klappt es in den Menü-Knopf zurück (Hysterese gegen Flackern). */
export const TOC_MENU_MAX_WIDTH = 1160;

/** Nächster scrollender Vorfahr — im Fenster scrollt der Fenster-Body, nicht `window`. */
const scrollHoehe = (el: HTMLElement | null): number | undefined => {
  if (typeof window === "undefined") return undefined;
  let cur: HTMLElement | null = el?.parentElement ?? null;
  while (cur) {
    const overflowY = window.getComputedStyle(cur).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return cur.clientHeight;
    cur = cur.parentElement;
  }
  return window.innerHeight;
};

export type TocLayoutResult = {
  layout: TocLayout;
  /** Höhe des scrollenden Vorfahren — `100vh` wäre im Fenster falsch. */
  availableHeight: number | undefined;
};

export const useTocLayout = (
  ref: React.RefObject<HTMLElement | null>,
  override: TocMode = "auto",
): TocLayoutResult => {
  const [breit, setBreit] = useState(false);
  const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);
  // Bucket-Gedächtnis für die Hysterese: nur beim echten Wechsel neu setzen,
  // sonst rendert jede Pixelbewegung des Fensterrahmens neu.
  const breitRef = useRef(false);

  // Ohne Verzeichnis gibt es nichts zu messen: der Aufstieg durch die Vorfahren
  // (`getComputedStyle` je Ereignis) liefe sonst bei jedem Resize umsonst.
  const aus = override === "none";

  /**
   * Das gemessene Element als **State**, nicht als `ref.current`.
   *
   * Grund: die Shell erscheint nicht zwingend im ersten Render. Die
   * öffentlichen Formulare starten mit `loading = true` und zeigen danach den
   * Willkommensflow — das Element mit dem Ref hängt also erst im dritten
   * Render-Zustand im DOM. Ein `RefObject` löst beim Belegen keinen Re-Render
   * aus; die Effekte unten wären genau einmal mit `ref.current === null`
   * gelaufen, hätten weder Erstmessung noch `ResizeObserver` angelegt, und die
   * Darstellung wäre für immer auf „menu" stehen geblieben (die dauerhafte
   * Sidebar der Kundenformulare wäre bei JEDER Breite verschwunden).
   *
   * Der Abgleich läuft ohne Dependency-Array nach jedem Render; ist der Wert
   * unverändert, steigt React aus dem `setState` aus — es entsteht keine
   * Schleife.
   */
  const [el, setEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setEl((prev) => (prev === ref.current ? prev : ref.current));
  });

  const messen = useCallback(() => {
    if (aus || !el) return;
    const w = el.getBoundingClientRect().width;
    const naechster = breitRef.current
      ? w > TOC_MENU_MAX_WIDTH
      : w >= TOC_SIDEBAR_MIN_WIDTH;
    if (naechster !== breitRef.current) {
      breitRef.current = naechster;
      setBreit(naechster);
    }
    setAvailableHeight((prev) => {
      const h = scrollHoehe(el);
      return h != null && prev !== h ? h : prev;
    });
  }, [el, aus]);

  // Erstmessung synchron vor dem Paint — sonst blitzt eine Darstellung auf,
  // die sofort wieder wechselt.
  useLayoutEffect(() => {
    messen();
  }, [messen]);

  useEffect(() => {
    if (aus || !el) return;
    if (typeof ResizeObserver === "undefined") {
      // jsdom / sehr alte Browser: einmal messen reicht, dann am Fenster hängen.
      alleFenster.addEventListener("resize", messen);
      return () => alleFenster.removeEventListener("resize", messen);
    }
    const ro = new ResizeObserver(() => messen());
    ro.observe(el);
    alleFenster.addEventListener("resize", messen);
    return () => {
      ro.disconnect();
      alleFenster.removeEventListener("resize", messen);
    };
  }, [el, messen, aus]);

  if (aus) return { layout: "none", availableHeight: undefined };
  return { layout: breit ? "sidebar" : "menu", availableHeight };
};
